import api from './api';

export interface DashboardStats {
    stats: {
        total_sales: number;
        total_orders: number;
        sku_count: number;
        report_count: number;
        active_sku_count: number;
    };
    activities: {
        id: number;
        title: string;
        time: string;
        user: string;
        type: string;
    }[];
}

export interface AmazonNewsItem {
    title: string;
    url: string;
    source: string;
    category: 'policy' | 'official' | 'service' | 'announcement' | 'news';
    icon: string;
    time?: string;
}

export interface AmazonNewsResponse {
    success: boolean;
    data: AmazonNewsItem[];
    cached: boolean;
    updated_at: string;
    is_mock?: boolean;
}

export interface ExchangeRateItem {
    currency: string;
    rate: number;
    date: string;
    change_percent: number | null;
}

export const dashboardService = {
    getStats: async (): Promise<DashboardStats> => {
        const response = await api.get('/dashboard/stats/');
        return response.data;
    },

    getAmazonNews: async (region: 'us' | 'cn' | 'all' = 'all'): Promise<AmazonNewsResponse> => {
        const response = await api.get(`/amazon-news/?region=${region}`);
        return response.data;
    },

    getCommunityNews: async (): Promise<CommunityNewsResponse> => {
        const response = await api.get('/dashboard/community-news/');
        return response.data;
    },

    getExchangeRates: async (): Promise<ExchangeRateItem[]> => {
        const response = await api.get('/exchange-rates/latest/?target=CNY&currencies=USD,GBP,EUR,JPY');
        return response.data;
    },

    getFreightIndices: async (): Promise<FreightIndexResponse> => {
        const response = await api.get('/dashboard/freight-indices/');
        return response.data;
    }
};

export interface FreightIndexItem {
    name: string;
    value: number;
    unit: string;
    trend: 'up' | 'down';
    desc: string;
}

export interface FreightIndexResponse {
    success: boolean;
    data: {
        ocean: FreightIndexItem[];
        air: FreightIndexItem[];
        update_time: string;
    };
}

export interface CommunityNewsItem {
    title: string;
    url: string;
    author: string;
    source: string;
    time: string;
}

export interface CommunityNewsResponse {
    success: boolean;
    data: CommunityNewsItem[];
    cached: boolean;
}

// 社区帖子详情 - 用于帖子列表页
export interface CommunityPostItem {
    id: number;
    title: string;
    url: string;
    author: string;
    source: string;
    time: string;
    view_count: number;
    reply_count: number;
    follow_count: number;
    scrape_status: 'pending' | 'scraped' | 'failed';
    is_hot: boolean;
    hot_rank: number | null;
    has_content: boolean;
    first_seen_at: string | null;
}

export interface CommunityPostsResponse {
    success: boolean;
    data: CommunityPostItem[];
    total: number;
    page: number;
    page_size: number;
}

// 单个帖子详情
export interface CommunityPostDetail {
    id: number;
    title: string;
    url: string;
    author: string;
    source: string;
    time: string;
    view_count: number;
    reply_count: number;
    follow_count: number;
    content_html: string;
    content_text: string;
    replies_html: string;
    scrape_status: 'pending' | 'scraped' | 'failed';
    scrape_error: string;
    first_seen_at: string | null;
    last_scraped_at: string | null;
}

export interface CommunityPostDetailResponse {
    success: boolean;
    data: CommunityPostDetail;
}

// 社区帖子服务
export const communityPostService = {
    // 获取帖子列表
    getPosts: async (
        page: number = 1,
        pageSize: number = 20,
        status?: string,
        search?: string,
        sortBy: string = 'reply_count',
        sortOrder: string = 'desc'
    ): Promise<CommunityPostsResponse> => {
        let url = `/dashboard/community-posts/?page=${page}&page_size=${pageSize}&sort_by=${sortBy}&sort_order=${sortOrder}`;
        if (status) {
            url += `&status=${status}`;
        }
        if (search) {
            url += `&search=${encodeURIComponent(search)}`;
        }
        const response = await api.get(url);
        return response.data;
    },

    // 获取帖子详情
    getPostDetail: async (postId: number): Promise<CommunityPostDetailResponse> => {
        const response = await api.get(`/dashboard/community-posts/${postId}/`);
        return response.data;
    },

    // 刷新帖子列表（重新抓取）
    refreshPosts: async (autoScrapeDetails: boolean = false): Promise<{ success: boolean; saved: number; updated: number }> => {
        const response = await api.post('/dashboard/community-posts/refresh/', {
            auto_scrape_details: autoScrapeDetails,
        });
        return response.data;
    },

    // 手动抓取帖子详情
    scrapePostDetail: async (postId: number): Promise<{ success: boolean; message?: string; error?: string }> => {
        const response = await api.post(`/dashboard/community-posts/${postId}/scrape/`);
        return response.data;
    },

    // 启动批量抓取
    startBulkScrape: async (pages: number = 5, force: boolean = false): Promise<{ success: boolean; message?: string; note?: string; error?: string }> => {
        const response = await api.post('/dashboard/community-posts/bulk-scrape/', { pages, force });
        return response.data;
    },

    // 获取抓取状态统计
    getScrapeStatus: async (): Promise<{ success: boolean; stats: { total: number; scraped: number; pending: number; failed: number } }> => {
        const response = await api.get('/dashboard/community-posts/scrape-status/');
        return response.data;
    },
};
